<script setup lang="ts">
import { computed } from 'vue'

const {
    opensAtMs,
    closesAtMs,
    serverNowMs,
    isDarkMode = false,
} = defineProps<{
    opensAtMs: number
    closesAtMs: number
    serverNowMs: number
    isDarkMode?: boolean
}>()

const remainingMs = computed(() => Math.max(0, closesAtMs - serverNowMs))

const remainingFraction = computed(() => {
    const totalMs = closesAtMs - opensAtMs
    if (totalMs <= 0) {
        return 0
    }
    return Math.min(1, remainingMs.value / totalMs)
})

const remainingSeconds = computed(() => Math.ceil(remainingMs.value / 1000))
const totalSeconds = computed(() => Math.max(0, Math.round((closesAtMs - opensAtMs) / 1000)))
const isRunningOut = computed(() => remainingMs.value > 0 && remainingMs.value <= 5000)
</script>

<template>
    <div
        class="live-timer-bar"
        role="timer"
        :aria-label="`${remainingSeconds} seconds left`"
    >
        <div
            class="live-timer-bar__track"
            :class="{ 'live-timer-bar__track--dark': isDarkMode }"
            role="progressbar"
            aria-label="Time remaining"
            aria-valuemin="0"
            :aria-valuemax="totalSeconds"
            :aria-valuenow="remainingSeconds"
        >
            <div
                class="live-timer-bar__fill"
                :class="{
                    'live-timer-bar__fill--dark': isDarkMode,
                    'live-timer-bar__fill--running-out': isRunningOut,
                }"
                :style="{ transform: `scaleX(${remainingFraction})` }"
            />
        </div>
        <span
            class="live-timer-bar__seconds"
            :class="{
                'live-timer-bar__seconds--dark': isDarkMode,
                'live-timer-bar__seconds--running-out': isRunningOut,
            }"
            aria-hidden="true"
        >
            {{ remainingSeconds }}s
        </span>
    </div>
</template>

<style lang="scss" scoped>
.live-timer-bar {
    display: flex;
    align-items: center;
    gap: 10px;

    &__track {
        flex: 1;
        height: 8px;
        border-radius: 4px;
        background: $pearl;
        overflow: hidden;

        &--dark {
            background: $moonlit-ocean;
        }
    }

    &__fill {
        width: 100%;
        height: 100%;
        border-radius: 4px;
        background: $brand-blue;
        transform-origin: left center;
        transition: transform 0.1s linear;

        &--dark {
            background: $banana-bread;
        }

        &--running-out {
            background: $brand-red;
        }
    }

    &__seconds {
        min-width: 32px;
        text-align: right;
        font-size: 14px;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        color: $brand-black;

        &--dark {
            color: $white;
        }

        &--running-out {
            color: $pepper;
        }

        &--running-out#{&}--dark {
            color: $rosa;
        }
    }
}
</style>
